'use client';
import React from 'react';

interface LoadingSpinnerProps {
  /** Whether the spinner overlay is visible */
  visible: boolean;
  /** Optional label shown below the spinner */
  label?: string;
}

/**
 * Full-screen overlay spinner — blocks all interaction while a task is in progress.
 * Usage: <LoadingSpinner visible={isLoading} label="Processing..." />
 */
export default function LoadingSpinner({ visible, label }: LoadingSpinnerProps) {
  if (!visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={label ?? 'Loading'}
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-black/50 backdrop-blur-sm"
    >
      {/* Outer ring */}
      <div className="relative flex items-center justify-center">
        <span className="absolute inline-block w-16 h-16 rounded-full border-4 border-primary/20" />
        {/* Spinning arc */}
        <span
          className="inline-block w-16 h-16 rounded-full border-4 border-transparent border-t-primary border-r-primary animate-spin"
          style={{ animationDuration: '0.75s' }}
        />
        {/* Inner dot */}
        <span className="absolute w-3 h-3 rounded-full bg-primary" />
      </div>

      {label && (
        <p className="mt-4 text-sm font-medium text-white tracking-wide select-none">
          {label}
        </p>
      )}
    </div>
  );
}
